import i18n from '@/i18n';
import { format } from 'date-fns';
import { taskAPI, getIpeWorkspaceMode } from '@/utils/api';
import { allocateTaskIdentifiersLocal } from '@/pages/ipe/workbench/utils/taskIdGenerator';
import { createIpeWorkflowTask } from '@/pages/ipe/workbench/utils/ipeWorkflowTaskRecord';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { trackPendingTask } from '@/utils/workbenchHistoryBadge';
import { GOODS_STUDIO_DEFAULT_PARAMS } from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { buildHistoryPanelRestoreSnapshot } from '@/pages/ipe/workbench/utils/workbenchHistoryPanelRestore';
import {
  buildGoodsLineageFields,
  buildPersistedGoodsPrintUrlMap,
  inferCropTaskIdFromImageIds,
  inferCutoutTaskIdFromImageIds,
  persistGoodsPrintUrlsToSession,
} from '@/pages/ipe/workbench/utils/goodsPrintUrlResolve';
import { buildTaskImageAssetsPayload } from '@/pages/ipe/workbench/utils/imageAssetModel';
import { appendLinkedGoodsTaskId } from '@/pages/ipe/workbench/utils/goodsExportLinkedComposites';
import { resolveGoodsStudioSubmitParams } from '@/pages/ipe/workbench/utils/goodsStudioSubmitUtils';

function buildAiGoodsEstimatedTotal(stampCount, studioParams) {
  const mainCount = Math.max(1, Number(studioParams?.mainImageCount) || 1);
  const detailCount = Math.max(0, Number(studioParams?.detailPageCount) || 0);
  return Math.max(1, stampCount) * (mainCount + detailCount);
}

function buildAiGoodsInitialResult(stampSnapshot, studioParams, operatorName = '') {
  const previewUrl = stampSnapshot[0]?.previewUrl || stampSnapshot[0]?.apiUrl || '';
  return {
    composeMode: 'ai',
    generationMode: 'goodsStudioAi',
    sourceNav: 'goods',
    billingNavId: 'goods',
    studioParams: { ...studioParams },
    stampPreviews: stampSnapshot.map((ref) => ref.previewUrl || ref.apiUrl || ''),
    stampCount: stampSnapshot.length,
    mainImageCount: studioParams.mainImageCount,
    detailPageCount: studioParams.detailPageCount,
    previewUrl,
    operator: operatorName,
    operatorName,
  };
}

/** 初始空套图骨架：Worker 完成后会覆写为含 compositeResults 的可导出结构 */
function buildAiGoodsPlaceholderGoodsForms(stampRefs, stampImageUrls = []) {
  return [{
    templateId: 'goods-studio-ai',
    templateName: '主图创作',
    imageGroups: stampRefs.map((ref, index) => ({
      [`印花${index + 1}`]: {
        id: ref.id,
        url: stampImageUrls[index] || ref.apiUrl || ref.previewUrl || '',
        name: ref.name || `印花${index + 1}`,
        role: 'print',
        assetId: ref.assetId,
      },
    })),
    compositeResults: [],
  }];
}

/**
 * 详情页联动「主图创作」（参数均可选填），创建任务并进入详情页。
 * @param {{ fromCropTaskId?: string, sourceGoodsTaskId?: string, sourceCopywritingTaskId?: string }} [params]
 *        sourceGoodsTaskId：上游 POD/主图任务 id，用于导出时双向关联成品
 *        sourceCopywritingTaskId：中间 AI 文案任务 id，用于入库回填标题
 */
export async function createAiGoodsTaskFromDetailSources({
  sources = [],
  studioParams = null,
  operatorName = '',
  fromCropTaskId = '',
  sourceGoodsTaskId = '',
  sourceCopywritingTaskId = '',
}) {
  const workspaceMode = getIpeWorkspaceMode();
  const stampRefs = (sources || [])
    .map((s) => {
      const id = String(s?.id || '').trim();
      const url = String(s?.url || s?.previewUrl || '').trim();
      if (!id || !url) return null;
      return {
        id,
        name: s.name || id,
        previewUrl: String(s.previewUrl || url).trim(),
        apiUrl: url,
        role: 'print',
        assetId: s.assetId,
      };
    })
    .filter(Boolean);

  if (stampRefs.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  // 参数解析与多图地址解析并行；多图彼此也并行，避免 3 张图串行等待
  const [params, stampImageUrls] = await Promise.all([
    resolveGoodsStudioSubmitParams({ ...GOODS_STUDIO_DEFAULT_PARAMS, ...(studioParams || {}) }),
    Promise.all(stampRefs.map(async (ref) => {
      const resolved = await resolveImg2ImgReferenceForApi(ref.apiUrl || ref.previewUrl);
      if (!resolved) {
        throw new Error(i18n.t('workbench:ipeUtilError.imageUnresolvedNamed', { name: ref.name }));
      }
      return resolved;
    })),
  ]);
  const estimatedTotal = buildAiGoodsEstimatedTotal(stampRefs.length, params);

  const rawPrintUrlMap = {};
  stampRefs.forEach((ref, index) => {
    rawPrintUrlMap[String(ref.id)] = stampImageUrls[index] || ref.apiUrl;
  });
  const stampIds = stampRefs.map((r) => r.id);
  const lineageCropTaskId = String(fromCropTaskId || '').trim()
    || inferCropTaskIdFromImageIds(stampIds);
  const lineageCutoutTaskId = lineageCropTaskId
    ? ''
    : inferCutoutTaskIdFromImageIds(stampIds);
  const sessionSourceTaskId = lineageCropTaskId || lineageCutoutTaskId;
  const printUrlMap = buildPersistedGoodsPrintUrlMap(rawPrintUrlMap, sessionSourceTaskId);
  persistGoodsPrintUrlsToSession(sessionSourceTaskId, printUrlMap);

  const placeholderForms = buildAiGoodsPlaceholderGoodsForms(stampRefs, stampImageUrls);
  const lineageFields = lineageCropTaskId
    ? buildGoodsLineageFields(lineageCropTaskId)
    : (lineageCutoutTaskId ? { sourceCutoutTaskId: lineageCutoutTaskId } : {});

  const linkedSourceTaskId = String(sourceGoodsTaskId || '').trim();
  const linkedCopywritingTaskId = String(sourceCopywritingTaskId || '').trim();

  // preview/stampPreviews 使用已落盘短链，避免 data URL 撑爆 result 触发拆表后 Worker 读到空 {}
  const imageAssets = buildTaskImageAssetsPayload({
    stampRefs,
    printUrlByImageId: printUrlMap,
    goodsForms: placeholderForms,
    sourceTaskId: sessionSourceTaskId,
    sourceModule: 'goods',
  });
  const initialResult = {
    ...buildAiGoodsInitialResult(stampRefs, params, operatorName),
    stampSourceUrls: stampImageUrls,
    stampPreviews: stampImageUrls,
    previewUrl: stampImageUrls[0] || '',
    goodsPrintUrlByImageId: printUrlMap,
    imageAssets,
    goodsForms: placeholderForms,
    serverWorker: true,
    batchPhase: 'queued',
    outputs: [],
    urls: [],
    processedStampIndices: [],
    ...(linkedSourceTaskId ? { sourceGoodsTaskId: linkedSourceTaskId } : {}),
    ...(linkedCopywritingTaskId
      ? {
        sourceCopywritingTaskId: linkedCopywritingTaskId,
        linkedAiCopywritingTaskIds: [linkedCopywritingTaskId],
      }
      : {}),
    ...lineageFields,
  };

  const { taskId, subAccount } = allocateTaskIdentifiersLocal('goods');
  const now = format(new Date(), 'yyyy-MM-dd HH:mm');
  const imageIds = stampRefs.map((ref) => ref.id);

  await createIpeWorkflowTask({
    id: taskId,
    type: 'goods',
    title: `主图创作 ${subAccount}`,
    name: '主图创作',
    subAccount,
    billingNavId: 'goods',
    imageIds,
    previewUrl: initialResult.previewUrl,
    status: 'processing',
    total: estimatedTotal,
    successCount: 0,
    failedCount: 0,
    result: initialResult,
    goodsForms: placeholderForms,
  });

  try {
    trackPendingTask('goodsStudio', taskId, workspaceMode);
    await taskAPI.submit(taskId);
    // 回写上游关联会 get+整包 update 商品设计大 result，不阻塞「带入」关闭弹窗
    if (linkedSourceTaskId) {
      void appendLinkedGoodsTaskId(linkedSourceTaskId, taskId);
    }
  } catch (error) {
    try {
      await taskAPI.update(taskId, {
        status: 'failed',
        failedCount: estimatedTotal,
        finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        result: {
          ...initialResult,
          failureHint: toIpeUserErrorMessage(error, '生成失败'),
        },
      });
    } catch {
      /* ignore */
    }
    throw error;
  }

  return {
    id: taskId,
    subAccount,
    billingNavId: 'goods',
    total: estimatedTotal,
    successCount: 0,
    failedCount: 0,
    status: 'processing',
    createdAt: now,
    finishedAt: '—',
    imageIds,
    previewUrl: initialResult.previewUrl,
    result: initialResult,
    goodsForms: placeholderForms,
    name: '主图创作',
    restoreHistoryPanel: buildHistoryPanelRestoreSnapshot('goods', {
      highlightTaskId: taskId,
    }),
  };
}
